import { useEffect, useState, useMemo } from "react";
import { api, formatApiError, fmtMoney } from "../../lib/api";
import { toast } from "sonner";
import { useLeague } from "./LeagueLayout";
import { RefreshCcw, Lock, DoorOpen, Check, AlertTriangle, X, Sparkles } from "lucide-react";
import { useRoosterResolve, PlayerNameLink } from "../../lib/playerLink";

export default function LeagueRinnovi() {
  const { league, teams, myTeam, isAdmin, reload } = useLeague();
  const [teamFilter, setTeamFilter] = useState(myTeam?.team_id || teams[0]?.team_id || "");
  const [players, setPlayers] = useState([]);
  const [pendingAgg, setPendingAgg] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const levels = league.settings?.renewal_levels || [];

  // Iter 71.5 — Blocco fase: i manager possono modificare rinnovi solo in Fase 8
  //   (Apertura Rinnovi). L'admin ha accesso sempre (deve poter approvare in Fase 9).
  const currentPhase = Number(league.season_phase ?? 0);
  const isRenewalPhase = currentPhase === 8 || currentPhase === 9;
  const managerLocked = !isAdmin && !isRenewalPhase;

  const load = async (tid) => {
    if (!tid) { setPlayers([]); return; }
    setLoading(true);
    try {
      const [r, pend] = await Promise.all([
        api.get(`/leagues/${league.league_id}/players`, { params: { team_id: tid } }),
        api.get(`/leagues/${league.league_id}/renewals/pending`),
      ]);
      setPlayers(r.data);
      setPendingAgg(pend.data.aggregate || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(teamFilter); /* eslint-disable-next-line */ }, [teamFilter]);

  // Iter 68: nome cliccabile → storico giocatore
  const roosterMap = useRoosterResolve(players.map(p => p.name));

  const setPending = async (player_id, action, level = null) => {
    try {
      await api.post(`/leagues/${league.league_id}/renewals/pending`, { player_id, action, level });
      toast.success(action === "svincola" ? "🚪 Bozza: svincolo" : action === "rinnova" ? `📝 Bozza: rinnovo L${level}` : "Bozza rimossa");
      await load(teamFilter);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const applyAll = async () => {
    if (!isAdmin) return;
    if (!window.confirm("⚠️ Rendere effettive TUTTE le modifiche in bozza? Questa operazione aggiorna budget, stipendi e rose per ogni squadra.")) return;
    setApplying(true);
    try {
      const r = await api.post(`/leagues/${league.league_id}/renewals/apply-pending`);
      toast.success(`✅ Applicate: ${r.data.rinnovi} rinnovi, ${r.data.svincoli} svincoli, ${fmtMoney(r.data.crediti_totali)} cr recuperati`);
      await reload();
      await load(teamFilter);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setApplying(false); }
  };

  const clearAll = async () => {
    if (!isAdmin) return;
    if (!window.confirm("Scartare TUTTE le bozze non ancora applicate?")) return;
    try {
      await api.delete(`/leagues/${league.league_id}/renewals/pending`);
      toast.success("🗑️ Bozze rimosse");
      await load(teamFilter);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const totalChanges = pendingAgg.reduce((s, a) => s + a.changes, 0);
  const totalCredits = pendingAgg.reduce((s, a) => s + a.credits_delta, 0);
  const totalSalaryDelta = pendingAgg.reduce((s, a) => s + a.salary_delta, 0);
  const myAgg = pendingAgg.find(a => a.team_id === teamFilter);

  // Iter 71.5 — Se il manager entra fuori dalla finestra Rinnovi, mostra il
  // contenuto in chiaroscuro con una notifica in cima invece di nasconderlo.
  const LockNotice = managerLocked ? (
    <div className="border border-amber-500/40 bg-gradient-to-r from-amber-500/15 to-slate-900 rounded-2xl p-4 flex items-start gap-3 shadow-lg shadow-amber-500/10" data-testid="renewals-phase-lock">
      <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center shrink-0">
        <Lock className="text-amber-300" size={18}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-amber-400 font-black">Sola lettura</div>
        <div className="text-sm font-bold text-white">Rinnovi ancora chiusi — solo anteprima</div>
        <p className="text-xs text-slate-400 mt-1 leading-snug">
          La finestra rinnovi apre alla <b className="text-amber-300">Fase 8 · Apertura Rinnovi</b>. Ora sei in <b>Fase {currentPhase}</b>.
          Puoi vedere la tua rosa, i livelli disponibili e le simulazioni, ma <b>non puoi salvare bozze</b>. Attendi che l&apos;admin avvii la fase.
        </p>
      </div>
    </div>
  ) : null;

  return (
    <div className="space-y-4">
      {LockNotice}
      <div className={managerLocked ? "space-y-4 opacity-60 pointer-events-none select-none" : "space-y-4"} data-testid={managerLocked ? "renewals-readonly-wrap" : "renewals-active-wrap"}>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.3em] text-amber-400 uppercase font-bold">🔄 Rinnovi & Svincoli</div>
          <h2 className="text-3xl font-black tracking-tight">Fine stagione</h2>
          <p className="text-slate-400 text-sm mt-1">Le modifiche restano in <strong className="text-amber-300">bozza</strong> finché l&apos;admin non le rende effettive.</p>
        </div>
        {(isAdmin || !myTeam) && (
          <select value={teamFilter} onChange={e=>setTeamFilter(e.target.value)}
            className="bg-slate-950 border border-slate-800 rounded-2xl px-3 py-2 text-sm shadow-inner"
            data-testid="renewal-team-filter">
            {teams.map(t => <option key={t.team_id} value={t.team_id}>⚽ {t.name}</option>)}
          </select>
        )}
      </div>

      {/* Pending changes banner */}
      {totalChanges > 0 && (
        <div className="border border-amber-500/40 rounded-3xl bg-gradient-to-br from-amber-500/10 via-slate-900 to-slate-900 p-5 shadow-2xl shadow-amber-500/10" data-testid="pending-banner">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-2xl bg-amber-500/20 flex items-center justify-center text-2xl border border-amber-500/40">📝</div>
              <div>
                <div className="font-black uppercase text-lg tracking-tight text-amber-300 flex items-center gap-2">
                  {totalChanges} modifica{totalChanges === 1 ? "" : "he"} in bozza
                </div>
                <div className="text-xs text-slate-400 mt-0.5">Saldo potenziale complessivo: {totalCredits >= 0 ? "+" : ""}{fmtMoney(totalCredits)} cr · Stipendi Δ {totalSalaryDelta >= 0 ? "+" : ""}{fmtMoney(totalSalaryDelta)}</div>
              </div>
            </div>
            {isAdmin && (
              <div className="flex gap-2">
                <button onClick={clearAll} data-testid="clear-pending-btn"
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-2xl border border-slate-700 hover:border-rose-500 hover:text-rose-300 text-xs font-bold uppercase shadow-md shadow-black/40">
                  <X size={14}/> Scarta
                </button>
                <button onClick={applyAll} disabled={applying} data-testid="apply-pending-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 text-slate-950 font-black uppercase text-xs shadow-lg shadow-emerald-500/40 hover:from-emerald-300 hover:to-emerald-500 disabled:opacity-50">
                  <Sparkles size={14}/> {applying ? "Applico…" : "Rendi effettive"}
                </button>
              </div>
            )}
            {!isAdmin && (
              <span className="text-[10px] uppercase tracking-widest text-slate-500 self-center">
                <AlertTriangle size={12} className="inline mr-1 text-amber-400"/> In attesa dell&apos;admin
              </span>
            )}
          </div>

          {/* per-team breakdown */}
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 mt-4">
            {pendingAgg.map(a => (
              <div key={a.team_id} className="rounded-2xl bg-slate-950/70 border border-slate-800 p-3 text-xs" data-testid={`pending-team-${a.team_id}`}>
                <div className="font-bold text-slate-300 truncate">⚽ {a.team_name}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{a.changes} modifica{a.changes === 1 ? "" : "he"}</div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-slate-500">Budget</span>
                  <span className="font-mono">{fmtMoney(a.budget)} → <strong className={a.potential_budget > a.budget ? "text-emerald-400" : a.potential_budget < a.budget ? "text-rose-400" : ""}>{fmtMoney(a.potential_budget)}</strong></span>
                </div>
                <div className="flex items-center justify-between text-slate-500">
                  <span>Stipendi Δ</span>
                  <span className={`font-mono ${a.salary_delta > 0 ? "text-rose-400" : a.salary_delta < 0 ? "text-emerald-400" : ""}`}>{a.salary_delta > 0 ? "+" : ""}{fmtMoney(a.salary_delta)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Levels legend */}
      <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-900/40 p-4 shadow-xl shadow-black/40">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">📋 Livelli disponibili (configurabili in Admin)</div>
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-2">
          {levels.map(l => (
            <div key={l.level} className={`rounded-2xl border p-3 shadow-md ${l.blindato ? "border-rose-500/40 bg-rose-500/10" : "border-emerald-500/30 bg-emerald-500/5"}`}>
              <div className="flex items-center gap-2 font-bold">
                {l.blindato ? <Lock size={14} className="text-rose-400"/> : <RefreshCcw size={14} className="text-emerald-400"/>}
                {l.label || `Livello ${l.level}`}
              </div>
              <div className="text-xs text-slate-400 mt-1">Stipendio: <strong>{(l.salary_pct * 100).toFixed(0)}%</strong> Qt.A</div>
              {!l.blindato && <div className="text-xs text-slate-500">Asta parte +{l.auction_bonus || 0} cr</div>}
              {l.blindato && <div className="text-xs text-rose-300">🔒 Non chiamabile all&apos;asta</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Team summary card (potential saldo) */}
      {myAgg && (
        <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 p-4 shadow-xl shadow-black/40" data-testid="team-potential">
          <div className="grid sm:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Budget attuale</div>
              <div className="font-mono font-black text-xl mt-1">{fmtMoney(myAgg.budget)} <span className="text-xs text-slate-500">cr</span></div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Budget potenziale</div>
              <div className={`font-mono font-black text-xl mt-1 ${myAgg.potential_budget > myAgg.budget ? "text-emerald-400" : myAgg.potential_budget < myAgg.budget ? "text-rose-400" : ""}`}>
                {fmtMoney(myAgg.potential_budget)} <span className="text-xs text-slate-500">cr</span>
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">Δ {myAgg.credits_delta >= 0 ? "+" : ""}{fmtMoney(myAgg.credits_delta)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Stipendi Δ</div>
              <div className={`font-mono font-black text-xl mt-1 ${myAgg.salary_delta > 0 ? "text-rose-400" : myAgg.salary_delta < 0 ? "text-emerald-400" : ""}`}>
                {myAgg.salary_delta > 0 ? "+" : ""}{fmtMoney(myAgg.salary_delta)}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">Impatto annuale sulla rosa</div>
            </div>
          </div>
        </div>
      )}

      {loading ? <div className="text-slate-500 py-10 text-center">Caricamento…</div> :
      players.length === 0 ? (
        <div className="border border-dashed border-slate-800 rounded-3xl p-10 text-center text-slate-500">
          <div className="text-4xl mb-2">🗂️</div>
          Nessun giocatore in rosa. Importa la rosa nella sezione <strong className="text-amber-400">Import</strong>.
        </div>
      ) : (
        <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-900/60 overflow-hidden shadow-xl shadow-black/40">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-slate-950/70">
                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="px-3 py-3">R</th>
                  <th className="px-3 py-3">Giocatore</th>
                  <th className="px-3 py-3 text-right">Qt.A</th>
                  <th className="px-3 py-3 text-right">Stip. attuale</th>
                  <th className="px-3 py-3">Bozza</th>
                  <th className="px-3 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {players.map(p => {
                  const pc = p.pending_change;
                  const isPendingRelease = pc?.action === "svincola";
                  const isPendingRenew = pc?.action === "rinnova";
                  const rowClass = isPendingRelease
                    ? "bg-rose-950/40 border-l-4 border-l-rose-500 line-through decoration-rose-400 decoration-2"
                    : isPendingRenew
                    ? "bg-emerald-950/30 border-l-4 border-l-emerald-500"
                    : "";
                  return (
                    <tr key={p.player_id} className={`border-t border-slate-800 hover:bg-slate-800/40 transition-colors ${rowClass}`} data-testid={`renewal-row-${p.player_id}`}>
                      <td className="px-3 py-2.5 no-underline">
                        <span className={`role-pill role-${p.role}`}>{p.role}</span>
                      </td>
                      <td className="px-3 py-2.5 font-bold">
                        <span className={isPendingRelease ? "text-rose-300" : ""}>
                          <PlayerNameLink name={p.name} fantaId={p.fanta_id} roosterMap={roosterMap} testid={`renewal-player-name-${p.player_id}`}/>
                        </span>
                        <span className="text-xs text-slate-500 ml-2 no-underline">{p.real_team}</span>
                        {isPendingRelease && (
                          <div className="text-[10px] text-rose-400 mt-0.5 no-underline">
                            🚪 Bozza svincolo · +{fmtMoney(pc.credits_delta)} cr al budget
                          </div>
                        )}
                        {isPendingRenew && (
                          <div className="text-[10px] text-emerald-400 mt-0.5 no-underline">
                            🔒 Bozza rinnovo L{pc.level} · nuovo stip. {fmtMoney(pc.salary_preview)}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono no-underline">{fmtMoney(p.quotazione_attuale)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-rose-300 no-underline">{fmtMoney(p.salary)}</td>
                      <td className="px-3 py-2.5 no-underline">
                        {pc ? (
                          <button onClick={()=>setPending(p.player_id, "mantieni")}
                            data-testid={`clear-${p.player_id}`}
                            className="text-[10px] font-bold px-2 py-1 rounded-full border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500">
                            <X size={10} className="inline"/> Rimuovi
                          </button>
                        ) : p.renewal_level > 0 ? (
                          <span className={`text-xs px-2 py-1 rounded-full border ${p.status === "blindato" ? "bg-rose-500/20 text-rose-300 border-rose-500/40" : "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"}`}>
                            {p.status === "blindato" ? `🔒 L${p.renewal_level}` : `L${p.renewal_level}`}
                          </span>
                        ) : <span className="text-slate-500 text-xs">—</span>}
                      </td>
                      <td className="px-3 py-2.5 no-underline">
                        <div className="flex flex-wrap gap-1">
                          {levels.map(l => (
                            <button key={l.level} onClick={()=>setPending(p.player_id, "rinnova", l.level)}
                              data-testid={`renew-${p.player_id}-L${l.level}`}
                              title={`Bozza: ${l.blindato ? 'Blindato ' : ''}rinnova L${l.level} — stip. ${(l.salary_pct*100).toFixed(0)}% Qt.A`}
                              className={`text-[10px] font-black px-2 py-1 rounded-full border transition-all hover:scale-105 ${
                                pc?.action === "rinnova" && pc.level === l.level
                                  ? "bg-emerald-500 text-slate-950 border-emerald-400 shadow-md shadow-emerald-500/40"
                                  : l.blindato ? "border-rose-500/40 text-rose-300 hover:bg-rose-500/20"
                                  : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20"
                              }`}>
                              {l.blindato ? `🔒 L${l.level}` : `L${l.level}`}
                            </button>
                          ))}
                          <button onClick={()=>setPending(p.player_id, "svincola")}
                            data-testid={`release-${p.player_id}`}
                            className={`text-[10px] font-bold px-2 py-1 rounded-full border transition-all hover:scale-105 ${
                              pc?.action === "svincola"
                                ? "bg-rose-500 text-white border-rose-400 shadow-md shadow-rose-500/40"
                                : "border-slate-600 hover:border-rose-500 text-slate-300 hover:text-rose-300"
                            }`}>
                            <DoorOpen size={10} className="inline"/> Svincola
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
